import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { allocationsApi } from '@/features/stock/api';
import { factsApi, type EventFacts, type EventFactsLedgerRow } from '@/features/insights/factsApi';
import { mergeLedgers, unaccountedOf } from '@/features/insights/engine';
import { movementsApi } from './movementsApi';
import type { XkEvent, XkEventStockStation, XkStock } from '@/lib/api/types';

export interface WarehouseLot {
  key: string;
  event: EventFacts;
  row: EventFactsLedgerRow;
  /** Unidades que siguen sin destino (ya restadas las disposiciones). */
  open: number;
  /** Unidades que salieron del juego: asignado − en juego − devuelto. */
  out: number;
}

/** Los cuatro contadores de un ítem, con una sola fuente para todas las pantallas. */
export interface StockCounters {
  /** Σ available en todas las asignaciones vivas. */
  inPlay: number;
  /** Σ available en eventos abiertos (sin cierre y sin terminar): lo comprometido. */
  committed: number;
  /** Entregado según el libro de premios de todos los eventos de la marca. */
  delivered: number;
  /** Lo que queda libre para asignar: restante − en juego. */
  headroom: number;
}

export interface WarehouseModel {
  owned: number;
  ownedValue: number | null;
  missingCost: XkStock[];
  committed: number;
  openEvents: number;
  restock: Array<{ s: XkStock; headroom: number }>;
  lots: WarehouseLot[];
  unaccounted: number;
  unaccountedValue: number | null;
  lastClosing: { at: string; name: string } | null;
  unclosedEnded: XkEvent[];
}

/**
 * El almacén de una marca, calculado de los mismos hechos que el informe de
 * cada evento: asignaciones vivas, eventos y su ledger. Lo usan el panel de
 * My Merch (tejas, lotes) y las tarjetas del catálogo, para que ningún
 * contador salga de dos fórmulas distintas.
 *
 * Todas las listas piden hasta 500 filas: `api.list` pagina a 25 por defecto
 * y una agregación por marca con la primera página miente sin avisar.
 */
export function useWarehouse(brandId: number | null, stocks: XkStock[]) {
  const allocsQ = useQuery({
    queryKey: queryKeys.stockAllocations.byBrand(brandId ?? 0),
    queryFn: () => allocationsApi.list({ companyId: brandId }, { perPage: 500 }),
    enabled: !!brandId,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId ?? 0),
    queryFn: () => api.list<XkEvent>('/xk-event', { filter: { companyId: brandId }, perPage: 500 }),
    enabled: !!brandId,
  });
  const eventIds = useMemo(() => (eventsQ.data?.items ?? []).map((e) => e.id).sort((a, b) => a - b), [eventsQ.data]);
  const factsQ = useQuery({
    queryKey: queryKeys.insights.facts(eventIds),
    queryFn: () => factsApi.forEvents(eventIds),
    enabled: eventIds.length > 0,
    staleTime: 30_000,
  });
  const movementsQ = useQuery({
    queryKey: queryKeys.stockMovements.recent,
    queryFn: () => movementsApi.recent(40),
    staleTime: 30_000,
  });

  const { model, perStock } = useMemo(() => {
    const now = Date.now();
    const events = eventsQ.data?.items ?? [];
    const eventById = new Map(events.map((e) => [e.id, e]));
    const facts = factsQ.data?.events ?? [];
    const allocs = ((allocsQ.data?.items ?? []) as XkEventStockStation[]).filter((a) => (a.status as string) !== 'Deleted');
    const isOpen = (eventId: number) => {
      const e = eventById.get(eventId);
      if (!e) return false;
      if (facts.find((f) => f.event.id === eventId)?.closing) return false;
      const end = e.endDate ? new Date(e.endDate).getTime() : null;
      return end == null || end >= now;
    };
    const inPlayByStock = new Map<number, number>();
    const committedByStock = new Map<number, number>();
    const openEvents = new Set<number>();
    for (const a of allocs) {
      inPlayByStock.set(a.stockId, (inPlayByStock.get(a.stockId) ?? 0) + (a.available ?? 0));
      if (isOpen(a.eventId)) {
        committedByStock.set(a.stockId, (committedByStock.get(a.stockId) ?? 0) + (a.available ?? 0));
        openEvents.add(a.eventId);
      }
    }
    const merged = new Map(mergeLedgers(facts).map((r) => [r.stockId, r]));
    const perStock = new Map<number, StockCounters>();
    for (const s of stocks) {
      const inPlay = inPlayByStock.get(s.id) ?? 0;
      perStock.set(s.id, {
        inPlay,
        committed: committedByStock.get(s.id) ?? 0,
        delivered: merged.get(s.id)?.delivered ?? 0,
        headroom: (s.quantityRemaining ?? 0) - inPlay,
      });
    }
    // Cartera: lo que la marca posee físicamente (almacén + en juego).
    const owned = stocks.reduce((acc, s) => acc + (s.quantityRemaining ?? 0), 0);
    const missingCost = stocks.filter((s) => s.unitCost == null);
    const ownedValue = missingCost.length
      ? null
      : stocks.reduce((acc, s) => acc + (s.quantityRemaining ?? 0) * Number(s.unitCost ?? 0), 0);
    const committed = [...committedByStock.values()].reduce((a, b) => a + b, 0);
    const restock = stocks
      .map((s) => ({ s, headroom: perStock.get(s.id)?.headroom ?? 0 }))
      .filter(({ s, headroom }) => headroom <= 0 || (!!s.restockThreshold && headroom <= (s.restockThreshold ?? 0)))
      .sort((a, b) => a.headroom - b.headroom);
    const lots: WarehouseLot[] = [];
    let lastClosing: { at: string; name: string } | null = null;
    for (const f of facts) {
      if (!f.closing) continue;
      if (!lastClosing || f.closing.closedAt > lastClosing.at) lastClosing = { at: f.closing.closedAt, name: f.event.name };
      for (const row of f.ledger) {
        const open = unaccountedOf(row);
        if (open <= 0) continue;
        lots.push({ key: `${f.event.id}:${row.stockId}`, event: f, row, open, out: Math.max(0, row.allocated - row.inPlay - row.returned) });
      }
    }
    lots.sort((a, b) => b.open - a.open);
    const unaccounted = lots.reduce((a, l) => a + l.open, 0);
    const unaccountedCostMissing = lots.some((l) => l.row.unitCost == null);
    const unaccountedValue = unaccountedCostMissing ? null : lots.reduce((a, l) => a + l.open * (l.row.unitCost ?? 0), 0);
    const unclosedEnded = events.filter((e) => {
      const end = e.endDate ? new Date(e.endDate).getTime() : null;
      return end != null && end < now && !facts.find((f) => f.event.id === e.id)?.closing;
    });
    const model: WarehouseModel = { owned, ownedValue, missingCost, committed, openEvents: openEvents.size, restock, lots, unaccounted, unaccountedValue, lastClosing, unclosedEnded };
    return { model, perStock };
  }, [stocks, allocsQ.data, eventsQ.data, factsQ.data]);

  return { model, perStock, allocsQ, eventsQ, factsQ, movementsQ };
}
